import { useEffect, useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { MermaidBlock } from '../ui/MermaidBlock';
import { ZoomablePane } from '../ui/ZoomablePane';
import {
  buildBusinessFlowMermaid,
  parseBusinessFlowFromStreamYaml,
  type BusinessFlowView,
} from './business-flow.utils';

interface BusinessFlowDialogProps {
  open: boolean;
  streamId: string;
  streamDisplayName?: string;
  onClose: () => void;
}

export function BusinessFlowDialog({
  open,
  streamId,
  streamDisplayName,
  onClose,
}: BusinessFlowDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flow, setFlow] = useState<BusinessFlowView | null>(null);

  useEffect(() => {
    if (!open || !streamId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFlow(null);
    void apiClient.get<{ yaml: string }>(`/api/admin/ai-config/streams/${encodeURIComponent(streamId)}`)
      .then((result) => {
        if (cancelled) return;
        const parsed = yaml.load(result.yaml);
        setFlow(parseBusinessFlowFromStreamYaml(parsed));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load business flow');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, streamId]);

  const mermaid = useMemo(() => (flow ? buildBusinessFlowMermaid(flow) : ''), [flow]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent maxWidth="5xl">
        <DialogHeader>
          <DialogTitle>
            Business Flow{streamDisplayName ? ` — ${streamDisplayName}` : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading flow
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : !flow ? (
            <p className="text-sm text-gray-500">
              This stream has no businessFlow yet. Add a `businessFlow` block to the stream YAML.
            </p>
          ) : (
            <>
              <div>
                <h3 className="text-base font-semibold">{flow.title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{flow.summary}</p>
              </div>
              {mermaid ? (
                <div className="flex h-[28rem] flex-col">
                  <ZoomablePane>
                    <MermaidBlock source={mermaid} />
                  </ZoomablePane>
                </div>
              ) : null}
              {flow.stages.length > 0 ? (
                <div>
                  <h4 className="mb-2 text-sm font-semibold">Stage machine</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="py-1 pr-3 font-medium">Stage</th>
                          <th className="py-1 pr-3 font-medium">Owner</th>
                          <th className="py-1 font-medium">Entry evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flow.stages.map((stage) => (
                          <tr key={stage.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-1.5 pr-3">{stage.label}</td>
                            <td className="py-1.5 pr-3">{stage.owner}</td>
                            <td className="py-1.5">{stage.entryEvidence || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2">
                {flow.actors.map((actor) => (
                  <div
                    key={actor.id}
                    className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="text-sm font-semibold">{actor.title}</div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{actor.youDo}</p>
                    {actor.youNever ? (
                      <p className="mt-1 text-sm text-gray-500">Never: {actor.youNever}</p>
                    ) : null}
                    {actor.talksTo?.length ? (
                      <p className="mt-1 text-xs text-gray-500">Talks to: {actor.talksTo.join(', ')}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
